import { XIcon } from "@phosphor-icons/react";
import type * as C from "@zooi/contracts";
import { createContext, type ReactNode, useContext, useEffect, useRef } from "react";
import { INBOX_LABELS as I } from "../../labels.ts";
import { Button, type Crumb, DetailPanel, Kbd, Page } from "../../ui.tsx";
import { ReviewBar } from "../../ui/review.tsx";

// The inbox's shared parts (docs/design-system.md §0.3, §0.4; plan M7 step 7g):
// the context the list gives its reviews, and the frame of a review beside it.

export type Intent = "approve" | "reject" | null;
export interface InboxControl {
  items: C.InboxItem[];
  intent: Intent;
  /** Close the review and return focus to the list. */
  close: () => void;
  /** The server confirmed a decision: report it and move to the next item. */
  done: (message: string) => void;
}
export const InboxContext = createContext<InboxControl | null>(null);
export function useInbox() {
  const control = useContext(InboxContext);
  if (!control) throw new Error("inbox_context_missing");
  return control;
}

export const narrow = () => window.matchMedia("(max-width: 860px)").matches;

/**
 * Where a review shows: beside the inbox list, or as its own page for a
 * decision that spans many objects (design system §0.3, two widths). A page
 * gives the crumbs to show above it.
 */
export type ReviewMode = { kind: "panel" } | { kind: "page"; crumbs: Crumb[] };
export const ReviewModeContext = createContext<ReviewMode>({ kind: "panel" });

/** Shared frame of a review: heading, the review itself, and the actions kept in view. */
export function Review({ label, title, meta, children, actions }: { label: string; title: ReactNode; meta?: ReactNode; children: ReactNode; actions?: ReactNode }) {
  const { close } = useInbox();
  const mode = useContext(ReviewModeContext);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (narrow()) heading.current?.focus(); }, [label]);
  if (mode.kind === "page") return <Page crumbs={mode.crumbs} wide>
    <div className="z-review-page">
      <header className="z-review-head"><h1 ref={heading} tabIndex={-1}>{title}</h1>{meta && <div className="z-detail-meta">{meta}</div>}</header>
      {children}
    </div>
    {actions && <ReviewBar><Button tone="ghost" onClick={close}>{I.close} <Kbd>Esc</Kbd></Button>{actions}</ReviewBar>}
  </Page>;
  return <DetailPanel label={label} onClose={close}>
    <div className="z-detail-head">
      <h2 ref={heading} tabIndex={-1}>{title}</h2>
      <button type="button" className="z-iconbtn" aria-label={I.close} title={`${I.close} (Esc)`} onClick={close}><XIcon size={16} /></button>
    </div>
    {meta && <div className="z-detail-meta">{meta}</div>}
    {children}
    {actions && <div className="z-detail-actions">{actions}</div>}
  </DetailPanel>;
}

export const metricNamer = (metrics: C.MetricSummary[] | undefined) => (id: string | null) => (id && metrics?.find((m) => m.definition.id === id)?.definition.name) || "–";

/** The detail column while a review loads or fails to load. */
export function DetailPanelShell({ children }: { children: ReactNode }) {
  const { close } = useInbox();
  const mode = useContext(ReviewModeContext);
  if (mode.kind === "page") return <Page crumbs={mode.crumbs}>{children}</Page>;
  return <DetailPanel label={I.detail} onClose={close}>
    <div className="z-detail-head"><span /><button type="button" className="z-iconbtn" aria-label={I.close} title={`${I.close} (Esc)`} onClick={close}><XIcon size={16} /></button></div>
    {children}
  </DetailPanel>;
}
